interface ProgressProps {
  value: number
  max?: number
  size?: 'sm' | 'md' | 'lg'
  color?: 'brand' | 'success' | 'warning' | 'error'
  showLabel?: boolean
  className?: string
}

const sizeClasses = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
}

const colorClasses = {
  brand: 'bg-[var(--color-brand-500)]',
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  error: 'bg-[var(--color-error)]',
}

export function Progress({
  value,
  max = 100,
  size = 'md',
  color = 'brand',
  showLabel = false,
  className = '',
}: ProgressProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={`${Math.round(percentage)}% complete`}
        className={`flex-1 w-full ${sizeClasses[size]} bg-[var(--color-surface-3)] rounded-full overflow-hidden`}
      >
        <div
          className={`h-full rounded-full transition-all duration-[var(--duration-slow)] ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-xs font-medium text-[var(--color-text-secondary)] min-w-[3ch] text-right">
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  )
}

interface RingProgressProps {
  value: number
  max?: number
  size?: number
  strokeWidth?: number
  color?: string
  className?: string
}

export function RingProgress({
  value,
  max = 100,
  size = 64,
  strokeWidth = 6,
  color = 'var(--color-brand-500)',
  className = '',
}: RingProgressProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100)
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-surface-3)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="transition-all duration-[var(--duration-slow)]"
        />
      </svg>
      <span className="absolute text-sm font-semibold text-[var(--color-text-primary)]">
        {Math.round(percentage)}%
      </span>
    </div>
  )
}
